import { z } from 'zod';
import { APPROVAL_STATUSES, CLIENT_STAGES, FILE_KINDS } from '../domain';

const person = z.object({ id: z.uuid(), name: z.string(), avatarUrl: z.string().nullable() });

/** Material (item de serviço da demanda) com o tempo já gasto nele. */
export const boardMaterialDto = z.object({
  id: z.uuid(),
  taskId: z.uuid(),
  taskTitle: z.string(),
  name: z.string(),
  quantity: z.number(),
  /** Minutos registrados (só a equipe vê; null para o cliente). */
  minutes: z.number().nullable(),
});
export type BoardMaterialDto = z.infer<typeof boardMaterialDto>;

export const deliveryFileDto = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.enum(FILE_KINDS),
  thumbnailUrl: z.string().nullable(),
  url: z.string(),
  versionNumber: z.number(),
});

/** Entrega do card inteiro para o cliente aprovar (uma por rodada). */
export const deliveryDto = z.object({
  id: z.uuid(),
  status: z.enum(APPROVAL_STATUSES),
  kind: z.enum(['project', 'task']),
  subjectId: z.uuid(),
  title: z.string(),
  round: z.number(),
  message: z.string().nullable(),
  requestedBy: z.string().nullable(),
  requestedAt: z.string(),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  comment: z.string().nullable(),
  files: z.array(deliveryFileDto),
});
export type DeliveryDto = z.infer<typeof deliveryDto>;

export const boardCardDto = z.object({
  kind: z.enum(['project', 'task']),
  id: z.uuid(),
  title: z.string(),
  color: z.string(),
  stage: z.enum(CLIENT_STAGES),
  /** Prazo combinado com o cliente (ISO). */
  dueAt: z.string().nullable(),
  pausedAt: z.string().nullable(),
  cancelled: z.boolean(),
  revisionCount: z.number(),
  stageChangedAt: z.string(),
  /** Motivo do último pedido de ajuste (quando está em ajuste). */
  lastChangeRequest: z.string().nullable(),
  pending: z.object({ id: z.uuid(), requestedAt: z.string(), round: z.number() }).nullable(),
  materials: z.array(boardMaterialDto),
  /** Tempo total registrado (null para o cliente). */
  minutes: z.number().nullable(),
  responsibles: z.array(person),
  progress: z.object({ total: z.number(), done: z.number() }).nullable(),
  /** Etapa interna do workflow (só a equipe). */
  statusName: z.string().nullable(),
  canMove: z.boolean(),
});
export type BoardCardDto = z.infer<typeof boardCardDto>;

export const clientBoardDto = z.object({ cards: z.array(boardCardDto) });
export type ClientBoardDto = z.infer<typeof clientBoardDto>;

export const clientBoardQuery = z.object({
  cancelled: z.enum(['0', '1']).default('0'),
});

export const clientBoardMoveInput = z.object({
  kind: z.enum(['project', 'task']),
  id: z.uuid(),
  stage: z.enum(CLIENT_STAGES),
  /** Arquivos da entrega (ao mover para Aguardando aprovação). */
  fileIds: z.array(z.uuid()).max(50).optional(),
  message: z.string().trim().max(2000).optional(),
});
export type ClientBoardMoveInput = z.infer<typeof clientBoardMoveInput>;

export const boardPauseInput = z.object({
  kind: z.enum(['project', 'task']),
  id: z.uuid(),
  paused: z.boolean(),
});

export const deliveryListQuery = z.object({
  clientId: z.uuid().optional(),
  kind: z.enum(['project', 'task']).optional(),
  subjectId: z.uuid().optional(),
  status: z.enum(APPROVAL_STATUSES).optional(),
});
